/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useMemo, useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { KTSVG, safeHttpUrl } from "../../../_metronic/helpers";
import { PageTitle } from "../../../_metronic/layout/core";
import axios from "axios";
import WeeklyReviews from "./WeeklyReviews";
import RatingDistribution from "./RatingDistribution";
import ReviewsByChannel from "./ReviewsByChannel";
import ReviewsByStatus from "./ReviewsByStatus";
import ReviewsWithText from "./ReviewsWithText";
import AverageRating from "./AverageRating";
import ProductsOverview from "./ProductsOverview";
import TotalReviews from "./TotalReviews";
import WeeklyReviewsDistribution from "./WeeklyReviewsDistribution";
import TopicsMap from "./TopicsMap";
import { Alert } from "react-bootstrap-v5";
import { Link, useHistory } from "react-router-dom";
import TryNewExperienceBanner from "../overview/TryNewExperienceBanner";
import { isNewExperienceEnabled } from "../overview/experience";
import NotificationStatsV2 from "./NotificationStatsV2";
import NotificationEffectivenessCard from "./NotificationEffectivenessCard";
import { MonthlyEffectiveness } from "./NotificationEffectivenessChart";
import DateRangeButton from "../../components/DateRangeButton";
import { format, subDays, max, parseISO } from "date-fns";
import { Granularity, aggregateNotificationStats, EffectivenessSummary } from "./statsUtils";
import OverdueAlert from "./OverdueAlert";
import { MenuComponent } from "../../../_metronic/assets/ts/components";

const MIN_DATE = parseISO("2025-12-01");
const ENABLE_NOTIFICATION_STATS = process.env.REACT_APP_ENABLE_NOTIFICATION_STATS === 'true';

interface CustomerModel {
  name: string;
  logo: string | undefined;
  storeUrl: string | undefined;
  settings: {
    enableStoreReview: boolean;
  };
}

interface SummaryStats {
  avgRating: number;
  reviewCount: number;
  pendingReviews: number;
  totalReviews: number;
  productCount: number;
  totalProducts: number;
  reviewsByRating: any;
  reviewsByStatus: any;
  reviewsWithText: any;
  reviewsByChannel: any;
}

interface ChartStats {
  reviewsByWeek: any;
  reviewsByWeekRating: any;
}

interface TopicStats {
  positiveReviewsTopics: any;
  negativeReviewsTopics: any;
}

interface StoreStatsModel {
  avgRating: number;
  reviewCount: number;
  pendingReviews: number;
  totalReviews: number;
  reviewsByWeek: any;
  reviewsByWeekRating: any;
  reviewsByRating: any;
  positiveReviewsTopics: any;
  negativeReviewsTopics: any;
}

const SKELETON_CSS = `
@keyframes skeletonPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
}
.dashboard-skeleton {
  background: var(--bs-gray-200);
  animation: skeletonPulse 1.5s ease-in-out infinite;
}
`;

const CardSkeleton: FC<{ height?: string; className?: string }> = ({
  height = "160px",
  className = "",
}) => (
  <div
    className={`card dashboard-skeleton ${className}`}
    style={{ height }}
  />
);

const ChartSkeleton: FC = () => (
  <div
    className="card dashboard-skeleton"
    style={{ height: "300px" }}
  />
);

const DashboardPage: FC = () => {
  const [customer, setCustomer] = useState<CustomerModel | undefined>(undefined);
  const [summaryStats, setSummaryStats] = useState<SummaryStats | undefined>(undefined);
  const [chartStats, setChartStats] = useState<ChartStats | undefined>(undefined);
  const [topicStats, setTopicStats] = useState<TopicStats | undefined>(undefined);
  const [storeStats, setStoreStats] = useState<StoreStatsModel | undefined>(undefined);

  const [isLoadingCustomer, setIsLoadingCustomer] = useState(true);
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const [isLoadingCharts, setIsLoadingCharts] = useState(true);
  const [isLoadingTopics, setIsLoadingTopics] = useState(true);
  const [isLoadingStoreStats, setIsLoadingStoreStats] = useState(false);
  const [isLoadingNotificationStats, setIsLoadingNotificationStats] = useState(false);

  const [notificationStats, setNotificationStats] = useState<any>(null);
  const [effectivenessSummary, setEffectivenessSummary] = useState<EffectivenessSummary>({ uniqueOrders: 0, reviews: 0, effectivenessRate: 0 });
  const [effectivenessMonthly, setEffectivenessMonthly] = useState<MonthlyEffectiveness[]>([]);
  const [currentTab, setCurrentTab] = useState(0);
  const intl = useIntl();

  const [granularity, setGranularity] = useState<Granularity>("week");
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>({
    start: max([subDays(new Date(), 90), MIN_DATE]),
    end: new Date(),
  });

  const last30DaysStart = subDays(new Date(), 30);
  const dateFilter = `startDate=${format(last30DaysStart, "yyyy-MM-dd")}&endDate=${format(new Date(), "yyyy-MM-dd")}`;

  async function loadNotificationStats(start: Date, end: Date) {
    setIsLoadingNotificationStats(true);
    try {
      const params = `startDate=${format(start, "yyyy-MM-dd")}&endDate=${format(end, "yyyy-MM-dd")}`;
      const [statsData, effectivenessData, effectivenessMonthlyData] = await Promise.all([
        axios.get(`notification-stats?${params}`),
        axios.get(`notification-effectiveness?${params}`),
        axios.get(`notification-effectiveness-monthly`),
      ]);
      setNotificationStats(statsData.data);
      setEffectivenessSummary(effectivenessData.data);
      setEffectivenessMonthly(effectivenessMonthlyData.data);
    } catch (error: any) {
      console.error('Error loading notification stats:', error);
    } finally {
      setIsLoadingNotificationStats(false);
    }
  }

  async function loadDashboard() {
    // 1. Dispara dashboard/info + stats/summary em paralelo — ambos retornam rápido
    try {
      const [customerResult, summaryResult] = await Promise.all([
        axios.get(`dashboard/info`),
        axios.get(`stats/summary`),
      ]);
      setCustomer(customerResult.data);
      setIsLoadingCustomer(false);
      setSummaryStats(summaryResult.data);
      setIsLoadingSummary(false);
    } catch (error: any) {
      console.error('Error loading dashboard summary:', error);
      setIsLoadingCustomer(false);
      setIsLoadingSummary(false);
    }

    // 2. Dispara stats/charts (aggregation temporal, ~5-10s) sem bloquear a UI
    axios.get(`stats/charts`).then((r) => {
      setChartStats(r.data);
      setIsLoadingCharts(false);
    }).catch((error) => {
      console.error('Error loading chart stats:', error);
      setIsLoadingCharts(false);
    });

    // 3. Dispara stats/topics (Atlas Search facets, ~10-20s) sem bloquear a UI
    axios.get(`stats/topics`).then((r) => {
      setTopicStats(r.data);
      setIsLoadingTopics(false);
    }).catch((error) => {
      console.error('Error loading topic stats:', error);
      setIsLoadingTopics(false);
    });

    // 4. Notification stats (inalterado)
    if (ENABLE_NOTIFICATION_STATS) {
      loadNotificationStats(
        max([subDays(new Date(), 90), MIN_DATE]),
        new Date()
      );
    }
  }

  const handleTabChange = (tab: number) => {
    setCurrentTab(tab);
    if (tab === 1 && storeStats === undefined && !isLoadingStoreStats) {
      setIsLoadingStoreStats(true);
      axios.get(`store-stats`).then((r) => {
        setStoreStats(r.data);
        setIsLoadingStoreStats(false);
      }).catch((error) => {
        console.error('Error loading store stats:', error);
        setIsLoadingStoreStats(false);
      });
    }
  };

  const handleDateRangeChange = (start: Date | null, end: Date | null) => {
    if (!start || !end) return;
    const clampedStart = max([start, MIN_DATE]);
    setDateRange({ start: clampedStart, end });
    loadNotificationStats(clampedStart, end);
  };

  const history = useHistory();

  // Teste A/B: quem optou pela nova tela, ou caiu na faixa de rollout, cai nela.
  useEffect(() => {
    if (isNewExperienceEnabled()) {
      history.replace("/overview");
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    if (summaryStats) {
      setTimeout(() => {
        MenuComponent.reinitialization();
      }, 100);
    }
  }, [summaryStats]);

  const chartNotificationStats = useMemo(
    () => aggregateNotificationStats(notificationStats, granularity, dateRange.start, dateRange.end),
    [notificationStats, granularity, dateRange]
  );

  const hasReviews = summaryStats && summaryStats.reviewCount > 0;

  return (
    <>
      <style>{SKELETON_CSS}</style>
      <OverdueAlert />
      <TryNewExperienceBanner />

      {/* Header do cliente */}
      {isLoadingCustomer ? (
        <div className="card dashboard-skeleton mb-5 mb-xl-10" style={{ height: "120px" }} />
      ) : customer ? (
        <div className="card mb-5 mb-xl-10">
          <div className="card-body pt-9 pb-0">
            <div className="d-flex flex-wrap flex-sm-nowrap mb-3">
              {customer.logo && (
                <div className="me-7 mb-4">
                  <div className="symbol symbol-100px symbol-lg-160px symbol-fixed position-relative">
                    <img src={customer.logo} alt={customer.name} style={{ objectFit: "contain" }} />
                  </div>
                </div>
              )}
              <div className="flex-grow-1">
                <div className="d-flex justify-content-between align-items-start flex-wrap mb-2">
                  <div className="d-flex flex-column">
                    <div className="d-flex align-items-center mb-4">
                      <a href="#" className="text-gray-800 text-hover-primary fs-2 fw-bolder me-1">
                        {customer.name}
                      </a>
                    </div>
                    <div className="d-flex flex-wrap fw-bold fs-6 mb-4 pe-2">
                      <KTSVG path="/media/icons/duotone/Map/Marker1.svg" className="svg-icon-4 me-1" />
                      Brasil
                    </div>
                    {safeHttpUrl(customer.storeUrl) && (
                      <div className="d-flex flex-wrap fw-bold fs-6 mb-4 pe-2">
                        <a href={safeHttpUrl(customer.storeUrl)} className="d-flex align-items-center text-hover-primary me-5 mb-2">
                          <KTSVG path="/media/icons/duotone/Home/Earth.svg" className="svg-icon-4 me-1" />
                          {customer.storeUrl}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Seletor de tabs (visível assim que summaryStats chegar) */}
      {!isLoadingSummary && customer?.settings?.enableStoreReview && (
        <div className="row g-6 g-xl-9 mb-10">
          <div className="text-end">
            <button
              className={`btn btn-sm ${currentTab === 0 ? "btn-secondary" : "btn-light-secondary"} me-2`}
              onClick={() => handleTabChange(0)}
            >
              {intl.formatMessage({ id: "page.dashboard.product-reviews", defaultMessage: "Avaliações de produtos" })}
            </button>
            <button
              className={`btn btn-sm ${currentTab === 1 ? "btn-secondary" : "btn-light-secondary"} me-2`}
              onClick={() => handleTabChange(1)}
            >
              {intl.formatMessage({ id: "page.dashboard.store-reviews", defaultMessage: "Avaliações da loja" })}
            </button>
          </div>
        </div>
      )}

      {/* ===== TAB 0: Avaliações de produtos ===== */}
      {currentTab === 0 && (
        <>
          {/* Cards de resumo — skeleton ou conteúdo */}
          {isLoadingSummary ? (
            <div className="row g-6 g-xl-9 mb-10">
              <div className="col-md-4"><CardSkeleton /></div>
              <div className="col-md-4"><CardSkeleton /></div>
              <div className="col-md-4"><CardSkeleton /></div>
            </div>
          ) : summaryStats && hasReviews ? (
            <>
              <div className="row g-6 g-xl-9 mb-10">
                <div className="col-md-4 col-sm-4 col-xl-4">
                  <TotalReviews published={summaryStats.reviewCount} total={summaryStats.totalReviews} />
                </div>
                <div className="col-md-4 col-sm-4 col-xl-4">
                  <AverageRating rating={summaryStats.avgRating} />
                </div>
                <div className="col-md-4 col-sm-4 col-xl-4">
                  <ProductsOverview count={summaryStats.productCount} total={summaryStats.totalProducts} />
                </div>
              </div>

              {/* Gráficos semanais — skeleton ou conteúdo */}
              {isLoadingCharts ? (
                <div className="row g-6 g-xl-9 mb-10">
                  <div className="col-md-12 col-xl-8"><ChartSkeleton /></div>
                  <div className="col-md-12 col-xl-4">
                    <RatingDistribution data={summaryStats.reviewsByRating} totalReviews={summaryStats.reviewCount} />
                  </div>
                </div>
              ) : chartStats ? (
                <div className="row g-6 g-xl-9 mb-10">
                  <div className="col-md-12 col-xl-8">
                    <WeeklyReviews data={chartStats.reviewsByWeek} />
                  </div>
                  <div className="col-md-12 col-xl-4">
                    <RatingDistribution data={summaryStats.reviewsByRating} totalReviews={summaryStats.reviewCount} />
                  </div>
                </div>
              ) : null}

              {isLoadingCharts ? (
                <div className="row g-6 g-xl-9 mb-10">
                  <div className="col-md-12"><ChartSkeleton /></div>
                </div>
              ) : chartStats ? (
                <div className="row g-6 g-xl-9 mb-10">
                  <div className="col-md-12 col-xl-12">
                    <WeeklyReviewsDistribution data={chartStats.reviewsByWeekRating} />
                  </div>
                </div>
              ) : null}

              {/* Tópicos (Atlas Search) — skeleton ou conteúdo */}
              {isLoadingTopics ? (
                <div className="row g-6 g-xl-9 mb-10">
                  <div className="col-md-12 col-xl-6"><CardSkeleton height="200px" /></div>
                  <div className="col-md-12 col-xl-6"><CardSkeleton height="200px" /></div>
                </div>
              ) : topicStats ? (
                <div className="row g-6 g-xl-9 mb-10">
                  {topicStats.positiveReviewsTopics[0]?.facet?.topicsFacet?.buckets?.length > 0 && (
                    <div className="col-md-12 col-xl-6">
                      <TopicsMap
                        data={topicStats.positiveReviewsTopics}
                        urlPrefix={`/reviews/all?status=all&${dateFilter}&topics=`}
                        title="Avaliações positivas - últimos 30 dias"
                      />
                    </div>
                  )}
                  {topicStats.negativeReviewsTopics[0]?.facet?.topicsFacet?.buckets?.length > 0 && (
                    <div className="col-md-12 col-xl-6">
                      <TopicsMap
                        data={topicStats.negativeReviewsTopics}
                        urlPrefix={`/reviews/all?status=all&${dateFilter}&topics=`}
                        title="Avaliações negativas - últimos 30 dias"
                      />
                    </div>
                  )}
                </div>
              ) : null}

              {/* Cards de distribuição */}
              <div className="row g-6 g-xl-9 mb-10">
                <div className="col-md-4 col-xl-4">
                  <ReviewsByChannel data={summaryStats.reviewsByChannel} />
                </div>
                <div className="col-md-4 col-xl-4">
                  <ReviewsByStatus data={summaryStats.reviewsByStatus} />
                </div>
                <div className="col-md-4 col-xl-4">
                  <ReviewsWithText data={summaryStats.reviewsWithText} />
                </div>
              </div>

              {/* Seção de notificações */}
              {ENABLE_NOTIFICATION_STATS && (
                <>
                  <div className="row g-6 g-xl-9 mb-5">
                    <div className="col-md-12">
                      <div className="card px-7 py-5 d-flex flex-row justify-content-between align-items-center flex-wrap gap-3">
                        <h3 className="fw-bolder text-dark mb-0">
                          {intl.formatMessage({ id: "page.dashboard.notifications-section.title", defaultMessage: "Notificações" })}
                        </h3>
                        <div className="d-flex align-items-center gap-4 flex-wrap">
                          <div className="d-flex align-items-center gap-2">
                            <span className="text-muted fs-7">
                              {intl.formatMessage({ id: "page.dashboard.group-by", defaultMessage: "Agrupar por:" })}
                            </span>
                            <div className="btn-group btn-group-sm">
                              {(["day", "week", "month"] as Granularity[]).map((g) => (
                                <button
                                  key={g}
                                  className={`btn btn-sm ${granularity === g ? "btn-primary" : "btn-light-primary"}`}
                                  onClick={() => setGranularity(g)}
                                >
                                  {intl.formatMessage({ id: `granularity.${g}`, defaultMessage: g })}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="separator separator-dashed h-25px d-none d-sm-block" style={{ width: 1 }} />
                          <div className="d-flex align-items-center gap-2">
                            <span className="text-muted fs-7">
                              {intl.formatMessage({ id: "page.dashboard.filter-period", defaultMessage: "Período:" })}
                            </span>
                            <DateRangeButton
                              initialDate={dateRange.start}
                              endDate={dateRange.end}
                              minDate={MIN_DATE}
                              onApplyDates={handleDateRangeChange}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row g-6 g-xl-9">
                    <div className="col-md-12">
                      <NotificationEffectivenessCard summary={effectivenessSummary} loading={isLoadingNotificationStats} monthlyData={effectivenessMonthly} />
                    </div>
                  </div>
                  <div className="row g-6 g-xl-9 mb-10">
                    <div className="col-md-12">
                      <NotificationStatsV2 source="email" title="E-mail" data={chartNotificationStats} loading={isLoadingNotificationStats} />
                    </div>
                  </div>
                  <div className="row g-6 g-xl-9 mb-10">
                    <div className="col-md-12">
                      <NotificationStatsV2 source="whatsapp" title="WhatsApp" data={chartNotificationStats} loading={isLoadingNotificationStats} />
                    </div>
                  </div>
                </>
              )}
            </>
          ) : !isLoadingSummary && summaryStats && !hasReviews ? (
            <Alert className="bg-light-primary border border-primary border-dashed d-flex flex-row">
              <div className="svg-icon svg-icon-2hx svg-icon-primary me-4 mb-5 mb-sm-0">
                <KTSVG path="/media/icons/duotone/Home/Bulb1.svg" />
              </div>
              <div className="d-flex flex-column pe-0 pe-sm-10 flex-grow-1">
                <h5>Sua loja ainda não tem avaliações publicadas</h5>
                {summaryStats.pendingReviews === 0 ? (
                  <div>Mas fique tranquilo, estamos trabalhando para coletar avaliações de seus consumidores.</div>
                ) : (
                  <div>
                    Você tem <strong>{summaryStats.pendingReviews} avaliações</strong> para moderar
                  </div>
                )}
              </div>
              {summaryStats.pendingReviews > 0 && (
                <Link to="/reviews/moderate" className="btn btn-primary">
                  Moderar avaliações
                </Link>
              )}
            </Alert>
          ) : null}
        </>
      )}

      {/* ===== TAB 1: Avaliações da loja ===== */}
      {currentTab === 1 && (
        <>
          {isLoadingStoreStats ? (
            <>
              <div className="row g-6 g-xl-9 mb-10">
                <div className="col-md-6"><CardSkeleton /></div>
                <div className="col-md-6"><CardSkeleton /></div>
              </div>
              <div className="row g-6 g-xl-9 mb-10">
                <div className="col-md-12 col-xl-8"><ChartSkeleton /></div>
                <div className="col-md-12 col-xl-4"><CardSkeleton height="300px" /></div>
              </div>
            </>
          ) : storeStats && storeStats.reviewCount > 0 ? (
            <>
              <div className="row g-6 g-xl-9 mb-10">
                <div className="col-md-6 col-sm-6 col-xl-6">
                  <TotalReviews published={storeStats.reviewCount} total={storeStats.totalReviews} />
                </div>
                <div className="col-md-6 col-sm-6 col-xl-6">
                  <AverageRating rating={storeStats.avgRating} />
                </div>
              </div>
              <div className="row g-6 g-xl-9 mb-10">
                <div className="col-md-12 col-xl-8">
                  <WeeklyReviews data={storeStats.reviewsByWeek} />
                </div>
                <div className="col-md-12 col-xl-4">
                  <RatingDistribution data={storeStats.reviewsByRating} totalReviews={storeStats.reviewCount} />
                </div>
              </div>
              <div className="row g-6 g-xl-9 mb-10">
                <div className="col-md-12 col-xl-12">
                  <WeeklyReviewsDistribution data={storeStats.reviewsByWeekRating} />
                </div>
              </div>
              <div className="row g-6 g-xl-9 mb-10">
                {storeStats.positiveReviewsTopics[0]?.facet?.topicsFacet?.buckets?.length > 0 && (
                  <div className="col-md-12 col-xl-6">
                    <TopicsMap
                      urlPrefix={`/store-reviews/all?status=all&${dateFilter}&topics=`}
                      data={storeStats.positiveReviewsTopics}
                      title="Avaliações positivas - últimos 30 dias"
                    />
                  </div>
                )}
                {storeStats.negativeReviewsTopics[0]?.facet?.topicsFacet?.buckets?.length > 0 && (
                  <div className="col-md-12 col-xl-6">
                    <TopicsMap
                      urlPrefix={`/store-reviews/all?status=all&${dateFilter}&topics=`}
                      data={storeStats.negativeReviewsTopics}
                      title="Avaliações negativas - últimos 30 dias"
                    />
                  </div>
                )}
              </div>
            </>
          ) : !isLoadingStoreStats ? (
            <Alert className="bg-light-primary border border-primary border-dashed d-flex flex-row">
              <div className="svg-icon svg-icon-2hx svg-icon-primary me-4 mb-5 mb-sm-0">
                <KTSVG path="/media/icons/duotone/Home/Bulb1.svg" />
              </div>
              <div className="d-flex flex-column pe-0 pe-sm-10 flex-grow-1">
                <h5>Sua loja ainda não tem avaliações publicadas</h5>
                {storeStats?.pendingReviews === 0 ? (
                  <div>Mas fique tranquilo, estamos trabalhando para coletar avaliações de seus consumidores.</div>
                ) : (
                  <div>
                    Você tem <strong>{storeStats?.pendingReviews} avaliações</strong> para moderar
                  </div>
                )}
              </div>
              {storeStats != null && storeStats.pendingReviews != null && storeStats.pendingReviews > 0 && (
                <Link to="/store-reviews/moderate" className="btn btn-primary">
                  Moderar avaliações
                </Link>
              )}
            </Alert>
          ) : null}
        </>
      )}
    </>
  );
};

const DashboardWrapper: FC = () => {
  const intl = useIntl();

  return (
    <>
      <PageTitle breadcrumbs={[]}>
        {intl.formatMessage({
          id: "menu.dashboard",
          defaultMessage: "Dashboard",
        })}
      </PageTitle>
      <DashboardPage />
    </>
  );
};

export { DashboardWrapper };
